<template>
  <div id="quiz">
    <div class="quiz-container">
      <UiBox :is-empty="isBoxEmpty" :tab-count="quizType.length">
        <template v-slot:nav-left>
          <UiTab
            v-for="(tab, index) in quizType"
            :key="index"
            :id="tab.type"
            :active="subType === tab.type"
            :data-id="`id-${index}`"
            @click="switchType(tab.type)"
          >
            <div class="label-box">
              <span class="label-shadow">{{ tab.name }}</span>
              <span class="label-text">{{ tab.name }}</span>
              <UiTipDot v-show="getTipDotVisibility(tab.type)" placement="label"></UiTipDot>
            </div>
          </UiTab>
        </template>
        <template v-slot:nav-right>
          <div class="nav-right-box">
            <UiButton class="modal-btn quiz-rule" colorType="red-complex" hook @click="handleRule">
              <i></i>
              <div class="label-box">
                <span class="label-shadow">竞猜规则</span>
                <span class="label-text">竞猜规则</span>
              </div>
            </UiButton>
            <UiButton class="modal-btn quiz-record" colorType="red-complex" hook  @click="handleHistory">
              <i></i>
              <div class="label-box">
                <span class="label-shadow">竞猜记录</span>
                <span class="label-text">竞猜记录</span>
              </div>
            </UiButton>
            <div class="switch-source-slot">
              <UiSelect :list="quizSchedule" :selected-id="subSchedule?.id" @switch="switchSchedule" />
            </div>
          </div>
        </template>
        <template v-slot:container>
          <QuizBox
            :subMatch="subMatch"
            :rounds="rounds"
            :subType="subType"
            :quizList="quizList"
            :quizSchedule="quizSchedule"
            :subSchedule="subSchedule"
            :isLoading="isLoadingQuiz"
          ></QuizBox>
        </template>
      </UiBox>
    </div>
    <!-- 竞猜相关弹窗 (统一由 store.openModal 触发, 各自内部 useModal 接管) -->
    <Betting></Betting>
    <History></History>
    <Rule></Rule>
    <Teams ref="teamsModal"></Teams>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide, useTemplateRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QuizBox from '@/views/quiz/layout/quiz-box'

import * as api from '@/service/index'
import UiBox from '@/components/ui-box';
import UiTab from '@/components/ui-tab';
import UiButton from '@/components/ui-button';
import UiTipDot from '@/components/ui-tip-dot.vue'
import UiSelect from "@/components/ui-select";
import Betting from '@/views/quiz/modal/betting'
import History from '@/views/quiz/modal/history'
import Rule from '@/views/quiz/modal/rule'
import { useAppStore } from '@/store'
import { usePrompt } from '@/composables/usePrompt'
import Teams from '@/views/schedule/teams'

const QUIZ_ROUTE_TYPE_MAP = Object.freeze({
  schedule: 'schedule',
  live: 'live',
  'rank-pk': 'rank_pk',
  'disuse-pk': 'disuse_pk'
})

const QUIZ_TYPE_ROUTE_MAP = Object.freeze({
  schedule: 'schedule',
  live: 'live',
  rank_pk: 'rank-pk',
  disuse_pk: 'disuse-pk'
})

const QUIZ_TYPE_STORAGE_KEY = 'sausage-match:quiz-type'

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()

const subType = ref('schedule')
const quizList = ref([])
// 默认 true: 首次进页面 seasonInfo 未 ready 时不会闪空态背景, 首个加载路径 (switchSchedule/switchType/getQuestionList) 会 reset false
const isLoadingQuiz = ref(true)

const defaultScheduleID = ref('')
const quizSchedule = ref([])
const subSchedule = ref({})

const rounds = ref([])
const subMatch = ref(0)
const subSeasonID = ref('')
const dataObj = ref({
  schedule: 1,
  live: 2,
  rank_pk: 3,
  disuse_pk: 4
})
// redPrompt: false

const quizType = ref([
  { name: '赛程竞猜', type: 'schedule' },
  { name: '实时竞猜', type: 'live' },
  { name: '排名PK', type: 'rank_pk' },
  { name: '淘汰PK', type: 'disuse_pk' }
])

const amount = computed(() => appStore.amount)
const token = computed(() => appStore.token)
const seasonInfo = computed(() => appStore.seasonInfo)
const showLingqu = computed(() => appStore.showLingqu)
// 无题目内容时切到空态背景 (loading 期间不判断, 避免 tab 切换时清空 → API resolve 之间闪空态)
const isBoxEmpty = computed(() => !isLoadingQuiz.value && (!quizList.value || quizList.value.length === 0))

// 模板 ref (弹窗改走 store.openModal + useModal, 不再需要 ref)
const teamsModal = useTemplateRef('teamsModal')

const { showPrompt } = usePrompt()

provide('switchSchedule', switchSchedule)
provide('handleQuiz', handleQuiz)
provide('switchType', switchType)
provide('switchMatch', switchMatch)
provide('handleHistory', handleHistory)
provide('handleRule', handleRule)
provide('handleResult', handleResult)

function getStoredQuizType() {
  if (typeof window === 'undefined') {
    return 'schedule'
  }
  const storedType = window.sessionStorage.getItem(QUIZ_TYPE_STORAGE_KEY) || ''
  return QUIZ_ROUTE_TYPE_MAP[storedType] || 'schedule'
}
function persistQuizType(type) {
  if (typeof window === 'undefined') {
    return
  }
  window.sessionStorage.setItem(QUIZ_TYPE_STORAGE_KEY, getQuizRouteType(type))
}
function normalizeQuizType(type) {
  return quizType.value.find((item) => item.type === type)?.type || 'schedule'
}
function normalizeQuizRouteType(type) {
  return QUIZ_ROUTE_TYPE_MAP[type] || getStoredQuizType()
}
function getQuizRouteType(type) {
  return QUIZ_TYPE_ROUTE_MAP[normalizeQuizType(type)] || 'schedule'
}
function syncQuizRoute(type) {
  const routeType = getQuizRouteType(type)
  if (route.params?.type === routeType) {
    return
  }
  router.replace({
    name: 'Quiz',
    params: {
      type: routeType
    },
    query: route.query,
    hash: route.hash
  })
}
async function initQuiz() {
  // 获取所有赛程
  if(!token.value) {
    isLoadingQuiz.value = false
    return
  }
  try {
    let response = await api.reqScheduleList({
      seasonId: subSeasonID.value,
      token: token.value
    })
    if (response.status === 200) {
      let list = []
      let schedule = response.data
      schedule = schedule && schedule.length !== 0 ? schedule : []
      appStore.schedule = schedule
      schedule.forEach((item) => {
        let time = formatDay(new Date(item.startAt))
        list.push({
          id: item.scheduleCode,
          name: `${time} ${item.name}`,
          startAt: item.startAt,
          isPresent: item.isPresent
        })
      })
      defaultScheduleID.value = list.find((item) => item.isPresent)?.id
      quizSchedule.value = list

      // ui-select 不再 auto-emit, 首次加载需手动选中默认赛程触发 subSchedule 赋值 →
      // 后续 quiz/list 与 team-list-by-schedule 请求才会跑
      if (defaultScheduleID.value) {
        switchSchedule(defaultScheduleID.value)
        return  // 交给 switchSchedule 内部管 loading
      }
    }
  } catch (e) {
    console.error(e)
  }
  // 未走到 switchSchedule 的路径 (无 defaultScheduleID / API 失败 / catch), reset loading
  isLoadingQuiz.value = false
}

// 入参， 赛程id   比赛id
async function getQuestionList(scheduleCode, matchCode, type) {
  if (scheduleCode == null) {
    isLoadingQuiz.value = false
    return
  }
  //  matchCode  {0:赛程竞猜,1,实时竞猜,2:?,3:?}无配置

  subMatch.value = matchCode
  quizList.value = []
  isLoadingQuiz.value = true
  let data = {
    scheduleCode: scheduleCode,
    matchCode: matchCode,
    token: token.value,
    seasonId: subSeasonID.value,
    quizType: dataObj.value[type] ? dataObj.value[type] : ''
  }
  let response = await api.reqQuestionList(data)
  if (response.status === 200) {
    let t_quizList = response.data

    // 判断返回的竞猜列表，未设置答案 && 时间有效 && 没有参与
    const valid = t_quizList.find((item) => item.isHint)
    if (valid) {
      appStore.isHintSchedule = true
    }

    // 获取该赛程参赛队伍
    // let promise_list = [];
    const promises = t_quizList.map(async (item) => {
      try {
        let teamList = []
        if (item.stype === 1) {
          teamList = await getSubScheduleTeams(scheduleCode, item.id)
        }
        return {
          ...item,
          teamList
        }
      } catch (error) {
        return {
          ...item,
          teamList: []
        }
      }
    })
    const newTQuizList = await Promise.all(promises)
    quizList.value = newTQuizList
  }
  isLoadingQuiz.value = false
}
async function getSubScheduleTeams(scheduleCode, questionId) {
  let response = await api.reqScheduleTeams({
    scheduleCode: scheduleCode,
    seasonId: subSeasonID.value,
    token: token.value,
    questionId: questionId || 0
  })
  if (response.status === 200) {
    // this.subScheduleTeams = response.data;
    return response.data
  } else {
    return []
  }
}

async function switchType(type, options = {}) {
  const { syncRoute = true, force = false } = options
  // console.log('我是quiz', type)
  if (type === subType.value && !force) {
    return
  }
  //
  subType.value = type
  persistQuizType(type)
  quizList.value = []
  isLoadingQuiz.value = true
  if (syncRoute) {
    syncQuizRoute(type)
  }

  appStore.reqRedDotState()

  // 渲染 Live函数  因函数作用域被提前，拿不到subSeasonID，改成箭头函数把
  const getLivedataBase = async (type) => {
    // console.log('我是live');
    // console.log('this.subSeasonID',this.subSeasonID)
    const dataObj = {
      seasonId: subSeasonID.value,
      scheduleCode: subSchedule.value.id,
      token: token.value
    }
    try {
      let { status, data } = await api.reqMatchList(dataObj)
      if (status === 200) {
        rounds.value = data
        // 找当前进行的局; 若没有 (后端可能都标 false 或结束后), fallback 到第一局, 保证 quiz/list 一定被调
        const targetMatch = rounds.value.find((item) => item.isPresent) || rounds.value[0]
        if (targetMatch) {
          getQuestionList(
            subSchedule.value.id,
            targetMatch.matchCode,
            type
          )
        } else {
          isLoadingQuiz.value = false
        }
      } else {
        isLoadingQuiz.value = false
      }
    } catch (err) {
      console.error(err)
      isLoadingQuiz.value = false
    }
  }
  // 排名PK
  switch (type) {
    case 'live':
    case 'disuse_pk':
    case 'rank_pk':
      getLivedataBase(type)
      break
    case 'schedule':
      {
        getQuestionList(subSchedule.value.id, 0, type)
      }
      break
    default:
  }
}
// 首次渲染的默认的数据，赛事竞猜
function switchSchedule(scheduleCode) {
  if (scheduleCode == null) {
    return
  }
  if (scheduleCode !== subSchedule.value.id) {
    subSchedule.value = quizSchedule.value.find(
      (item) => item.id === scheduleCode
    )
    // this.showSelect = false;

    if (subType.value !== 'schedule') {
      switchType(subType.value, { syncRoute: false, force: true })
    } else {
      // 获取该赛程code的竞猜列表
      getQuestionList(scheduleCode, subMatch.value, subType.value)
    }

    // 埋点
    if (typeof tapdb === 'undefined') return
    tapdb.trackEvent('切换阶段')
  }
}
async function switchMatch(matchCode) {
  // console.log('2222', this.subType);
  if (matchCode !== subMatch.value) {
    // 选择第几局的时候，传一下当前的类别this.dateObj
    // console.log('Inning',this.dataObj);
    // console.log('Inning', this.subType)
    getQuestionList(subSchedule.value.id, matchCode, subType.value)
  }
}
function getDateTime(date) {
  return new Date(date).getTime()
}
async function handleQuiz(quizInfo, selectInfo, answerInfo, index) {
  // console.log('selectInfo',selectInfo, index);
  // console.log('竞猜的也的',selectInfo.answers[index].popularity);
  if (quizInfo.answer && quizInfo.answer !== 0) {
    showPrompt('本场竞猜已结束')
    return
  }

  // 获取服务器时间
  let currentTime = new Date()
  let result = await api.reqcurrentTime()
  if (result.status === 200) {
    currentTime = result.data
  }
  const getServerCurrentTime = getDateTime(currentTime)
  if (getServerCurrentTime >= getDateTime(quizInfo.endAt)) {
    showPrompt('本场竞猜已结束')
    return
  }

  if (getServerCurrentTime < getDateTime(quizInfo.startAt)) {
    showPrompt('本场竞猜未开始')
    return
  }
  // 竞猜币上限
  if (amount.value.quizRemainNum >= 99999) {
    showPrompt('竞猜币已达上限,无法继续竞猜')
    return
  }
  // console.log('handleQuiz');
  // console.log('quizInfo.type',quizInfo);
  // console.log('quizInfo.stype',selectInfo);
  // console.log('handleQuiz');
  let obj = {}
  let other = {}
  if (quizInfo.type === 1 && quizInfo.stype === 1) {
    // 战队预测
    obj = {
      question_id: quizInfo.id,
      lossRate: selectInfo.lossRate || 0,
      obj_avatar: selectInfo.avatar,
      obj_name: selectInfo.name,
      answer_code: selectInfo.teamCode,
      answer_name: selectInfo.name,
      ratios: selectInfo.popularity
    }

    other = {
      stype: 1,
      subType: subType.value,
      schedule_id: subSchedule.value.name,
      match_title: quizInfo.title,
      match_id: subMatch.value,
      type: ''
    }
  } else if (quizInfo.type === 2 && quizInfo.stype === 2) {
    let objInfo = selectInfo.objList[index] || {}
    // 单个预测
    obj = {
      question_id: quizInfo.id,
      lossRate: answerInfo.lossRate || 0,
      obj_avatar: objInfo.avatar || '',
      obj_name: objInfo.name || '',
      answer_code: answerInfo.answerCode,
      answer_name: answerInfo.answer,
      ratios: selectInfo.answers[index].popularity
    }

    other = {
      stype: 2,
      subType: subType.value,
      schedule_id: subSchedule.value.name,
      match_title: quizInfo['questionDetail']['title'],
      match_id: subMatch.value,
      type: quizInfo.title
    }
  } else if (quizInfo.type === 3 && quizInfo.stype === 2) {
    let objInfo = selectInfo.objList[index]
    // console.log('123');
    obj = {
      question_id: quizInfo.id,
      lossRate: answerInfo.lossRate || 0,
      obj_avatar: objInfo.avatar || '',
      obj_name: objInfo.name || '',
      answer_code: answerInfo.answerCode,
      answer_name: answerInfo.answer,
      ratios: selectInfo.answers[index].popularity
    }
    // 当前竞猜队伍信息（后台配置的对应信息）
    other = {
      stype: 2,
      subType: subType.value,
      schedule_id: subSchedule.value.name,
      match_title: quizInfo['questionDetail']['title'],
      match_id: subMatch.value,
      type: quizInfo.title
    }

  } else if (quizInfo.type === 4 && quizInfo.stype === 2) {
    // 4: 淘汰PK
    // console.log('456');
    let objInfo = selectInfo.objList[index]
    // console.log('obj let objInfo = selectInfo.objList[0]ect',objInfo);
    // console.log('objInfo',objInfo);
    // 当前竞猜队伍信息，左右赔率信息等
    obj = {
      question_id: quizInfo.id,
      lossRate: answerInfo.lossRate || 0,
      obj_avatar: objInfo.avatar || '',
      obj_name: objInfo.name || '',
      answer_code: answerInfo.answerCode,
      answer_name: answerInfo.answer,
      ratios: selectInfo.answers[index].popularity
    }
    // 当前竞猜队伍信息（后台配置的对应信息）
    other = {
      stype: 2,
      subType: subType.value,
      schedule_id: subSchedule.value.name,
      match_title: quizInfo['questionDetail']['title'],
      match_id: subMatch.value,
      type: quizInfo.title
    }
  }
  // obj 获取下注信息，other 获取埋点信息
  appStore.openModal({ id: 'quiz-betting', params: { info: obj, other } })
  // 埋点
  if (typeof tapdb === 'undefined') {
    return
  }
  //   埋点函数 排名PK 淘汰PK
  const buriedFunctions = (subType, type) => {
    const raceType = {
      schedule: '赛程竞猜',
      live: '实时竞猜',
      rank_pk: '排名PK',
      disuse_pk: '淘汰PK'
    }
    switch (type) {
      case 1:
        {
          tapdb.trackEvent(raceType[subType], {
            '#schedule_id': subSchedule.value.name,
            '#match_title': quizInfo.title,
            '#match_id': subMatch.value
          })
        }
        break
      case 2:
        {
          tapdb.trackEvent(raceType[subType], {
            '#schedule_id': subSchedule.value.name,
            '#match_title': quizInfo['questionDetail']['title'],
            '#match_id': subMatch.value,
            '#type': quizInfo.title
          })
        }
        break
      default:
    }
  }
  buriedFunctions(subType.value, quizInfo.stype)
}
function formatDay(dateObj) {
  let month = dateObj.getMonth() + 1
  month = month < 10 ? '0' + month : month
  let day = dateObj.getDate()
  day = day < 10 ? '0' + day : day

  return `${month}/${day}`
}
function handleHistory() {
  appStore.openModal({ id: 'quiz-history' })

  // 埋点
  if (typeof tapdb === 'undefined') return
  tapdb.trackEvent('竞猜记录')
}

function handleRule() {
  appStore.openModal({ id: 'quiz-rule' })

  // 埋点
  if (typeof tapdb === 'undefined') return
  tapdb.trackEvent('竞猜规则')
}
function handleResult(info) {
  const { answers, teamList } = info
  let resultTeams = answers.map((item) => {
    let subTeam = teamList.find((o) => item.answerCode === o.teamCode)
    return subTeam
  })
  // console.log('resultTeams',resultTeams);
  // console.log('subScheduleTeams',this.subScheduleTeams);
  teamsModal.value.initTeams(resultTeams, '本场晋级')
}
function getTipDotVisibility(type) {
  const visibilityMap = {
    schedule:showLingqu.value.quizHots.quizSchedule,
    live:showLingqu.value.quizHots.quizReal,
    rank_pk:showLingqu.value.quizHots.quizRank,
    disuse_pk :showLingqu.value.quizHots.quizElimi
  };
  return visibilityMap[type] || false;
}
watch(
  () => route.params.type,
  (newVal) => {
    const nextType = normalizeQuizRouteType(newVal)
    if (route.params?.type !== getQuizRouteType(nextType)) {
      syncQuizRoute(nextType)
    }
    if (!quizSchedule.value.length || !subSchedule.value.id) {
      subType.value = nextType
      return
    }
    if (nextType !== subType.value) {
      switchType(nextType, { syncRoute: false, force: true })
    }
  },
  { immediate: true }
)
watch(
  seasonInfo,
  (newVal) => {
    if (newVal && newVal.id) {
      subSeasonID.value = newVal.id
      // 获取当前赛季的参赛队伍
      initQuiz()
      // this.fetchActivityList()
    }
  },
  { immediate: true, deep: true }
)
</script>

<style lang="scss" scoped>
#quiz {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: get_percentage(730);
  position: relative;
  .quiz-container {
    flex: 1;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .nav-right-box {
      height: 100%;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .modal-btn {
        @include r(width, 130);
        @include r(height, 40);
        @include r(font-size, 20);
        @include r(margin-right, 6);
        -webkit-tap-highlight-color: transparent;
        cursor: pointer;
        @include boxShadowSet_Btn;
        i {
          display: inline-block;
          @include r(width, 25);
          @include r(height, 25);
          @include r(margin-right, 5);
        }
        &.quiz-rule i {
          @include icon_status('btn_quiz_rule');
        }
        &.quiz-record i {
          @include icon_status('btn_quiz_record');
        }
      }
      .switch-source-slot {
        position: relative;
        @include r(width, 160);
        @include r(height, 36);

        // 赛程名较长, 触发器字号缩到 12; ellipsis / arrow 让位由 ui-select 通用提供
        :deep(.ui-select-trigger) {
          @include r(font-size, 14);
          .inner-box {
            @include r(padding-right, 22);
            .label-box {
              box-sizing: border-box;
              .label-shadow, .label-text {
                white-space: pre-line;
                line-height: 1;
              }
            }
          }
        }
        :deep(.ui-select-panel) {
          ul li {
            @include r(font-size, 18);
          }
          @include r(width, 240);
          @include r(max-height, 464);
          overflow: hidden auto;
        }
      }
    }
  }
}
</style>
